import type { Component, ComponentFactory } from "./types";

const registry = new Map<string, ComponentFactory>();
const instances = new WeakMap<HTMLElement, Component>();

export function define<T extends Component>(
  name: string,
  factory: ComponentFactory<T>
): void {
  if (registry.has(name)) {
    console.warn(`[registry] "${name}" is already registered — skipping.`);
    return;
  }
  registry.set(name, factory as ComponentFactory);
}

export function mount(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-component]").forEach(mountEl);
}

export function mountEl(el: HTMLElement): void {
  if (instances.has(el)) return;

  const name = el.dataset.component;
  if (!name) return;

  const factory = registry.get(name);
  if (!factory) {
    if (import.meta.env.DEV) {
      console.warn(`[registry] No component registered for: "${name}"`);
    }
    return;
  }

  instances.set(el, factory(el));
}

export function unmount(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-component]").forEach(unmountEl);
}

export function unmountEl(el: HTMLElement): void {
  instances.get(el)?.destroy?.();
  instances.delete(el);
}

/** Retrieve a typed instance for inter-component communication */
export function getInstance<T extends Component>(
  el: HTMLElement
): T | undefined {
  return instances.get(el) as T | undefined;
}
